import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Scales, CheckCircle, CaretDown, Warning } from "@phosphor-icons/react";

/** Pannello "Verifica normativa": mostra cosa manca e permette di allineare senza perdere nulla. */
export default function VerificaNormativa({ onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [espanso, setEspanso] = useState(null); // slug tipo espanso

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/normativa/verifica");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore verifica");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const allinea = async () => {
    if (!window.confirm(
      `Aggiungere le ${data.totale_voci_da_aggiungere} voci mancanti a ${data.totale_tipi_da_aggiornare} tipi di incarico e ${data.totale_pratiche_da_aggiornare} incarichi aperti?\n\nNulla di esistente viene modificato o rimosso: le fasi e i controlli già spuntati restano come sono.`
    )) return;
    setApplying(true);
    try {
      const { data: res } = await api.post("/normativa/allinea", { tipi: true, pratiche: true });
      toast.success(`Aggiunte ${res.voci_aggiunte} voci · ${res.tipi_aggiornati} tipi e ${res.pratiche_aggiornate} incarichi aggiornati`);
      await load();
      onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore allineamento");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return <div className="rounded-lg border border-slate-200 bg-white px-5 py-4 text-sm text-slate-400 font-mono">Verifica normativa in corso...</div>;
  }
  if (!data) return null;

  const allineato = data.totale_voci_da_aggiungere === 0;

  return (
    <div className={`rounded-lg border overflow-hidden mb-6 ${allineato ? "border-emerald-200 bg-emerald-50/40" : "border-amber-300 bg-amber-50/50"}`} data-testid="verifica-normativa">
      <div className="px-5 py-4 flex items-start gap-4">
        <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${allineato ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
          {allineato ? <CheckCircle size={18} weight="fill" /> : <Scales size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Verifica normativa · Toscana</div>
          <div className="font-heading text-base font-bold text-slate-900">
            {allineato
              ? "Tipi di incarico e incarichi allineati alla normativa"
              : `${data.totale_voci_da_aggiungere} voci obbligatorie mancanti`}
          </div>
          <div className="text-xs text-slate-600 mt-1">
            {allineato
              ? "Fasi, controlli e scadenze previsti da normativa sono tutti presenti."
              : `${data.totale_tipi_da_aggiornare} tipi di incarico e ${data.totale_pratiche_da_aggiornare} incarichi aperti da completare con fasi, controlli e scadenze previsti da DPR 380/01, D.Lgs 81/08, L.R.T. 65/2014 e norme collegate.`}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={load} data-testid="normativa-refresh">Ricontrolla</Button>
          {!allineato && (
            <Button size="sm" onClick={allinea} disabled={applying} data-testid="normativa-allinea-btn">
              {applying ? "Aggiornamento..." : "Aggiungi le voci mancanti"}
            </Button>
          )}
        </div>
      </div>

      {!allineato && (
        <div className="border-t border-amber-200 divide-y divide-amber-100 bg-white/70 max-h-80 overflow-y-auto">
          {data.tipi.map((t) => {
            const tot = t.fasi_mancanti.length + t.checklist_mancanti.length + t.scadenze_mancanti.length;
            const aperto = espanso === t.tipo;
            return (
              <div key={t.tipo} data-testid={`normativa-tipo-${t.tipo}`}>
                <button
                  type="button"
                  onClick={() => setEspanso(aperto ? null : t.tipo)}
                  className="w-full px-5 py-2.5 flex items-center gap-3 hover:bg-amber-50/60 text-left"
                >
                  <Warning size={14} className="text-amber-600 shrink-0" />
                  <span className="text-sm font-semibold text-slate-900 flex-1 truncate">{t.label}</span>
                  <span className="text-[11px] font-mono text-slate-500 shrink-0">
                    +{t.fasi_mancanti.length} fasi · +{t.checklist_mancanti.length} controlli
                    {t.scadenze_mancanti.length > 0 && ` · +${t.scadenze_mancanti.length} scadenze`}
                  </span>
                  <CaretDown size={12} className={`text-slate-400 shrink-0 transition-transform ${aperto ? "rotate-180" : ""}`} />
                </button>
                {aperto && (
                  <div className="px-5 pb-3 pt-1 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    {t.fasi_mancanti.length > 0 && (
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">Fasi da aggiungere ({t.fasi_mancanti.length})</div>
                        <ul className="space-y-1 text-slate-700">
                          {t.fasi_mancanti.map((f, i) => <li key={i} className="leading-snug">· {f}</li>)}
                        </ul>
                      </div>
                    )}
                    {t.checklist_mancanti.length > 0 && (
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">Controlli da aggiungere ({t.checklist_mancanti.length})</div>
                        <ul className="space-y-1 text-slate-700">
                          {t.checklist_mancanti.map((c, i) => <li key={i} className="leading-snug">· {c}</li>)}
                        </ul>
                      </div>
                    )}
                    {t.scadenze_mancanti.length > 0 && (
                      <div className="md:col-span-2">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">Scadenze da aggiungere</div>
                        <ul className="space-y-1 text-slate-700">
                          {t.scadenze_mancanti.map((s, i) => <li key={i} className="leading-snug">· {s.nome} — entro {s.giorni} giorni ({s.note})</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {data.pratiche.length > 0 && (
            <div className="px-5 py-3 text-xs text-slate-600">
              <span className="font-semibold text-slate-900">{data.pratiche.length} incarichi aperti</span> riceveranno le fasi e i controlli mancanti del proprio tipo, senza toccare quelli già spuntati.
            </div>
          )}
        </div>
      )}

      <div className="px-5 py-2 text-[10px] text-slate-400 leading-relaxed border-t border-slate-200 bg-white/60">
        {data.disclaimer}
      </div>
    </div>
  );
}
